import React, {useMemo} from 'react';
import {AbsoluteFill, useCurrentFrame, useVideoConfig} from 'remotion';
import type {HighlightItem} from '../../templates/custom/video-highlight/types';
import {ArrowMarker} from './ArrowMarker';
import {ShapeMarker} from './ShapeMarker';
import {
  DEFAULT_ARROW_COLOR,
  DEFAULT_MARKER_COLOR,
  getMarkerOpacityAtTime,
  isHighlightActiveAtTime,
  resolveMarkerRect,
} from './marker-utils';

export interface HighlightOverlayProps {
  highlights: HighlightItem[];
  defaultFadeIn?: number;
  defaultFadeOut?: number;
  currentTimeInSeconds?: number;
  coordinateWidth?: number | null;
  coordinateHeight?: number | null;
}

export const HighlightOverlay: React.FC<HighlightOverlayProps> = ({
  highlights,
  defaultFadeIn = 0,
  defaultFadeOut = 0,
  currentTimeInSeconds,
  coordinateWidth,
  coordinateHeight,
}) => {
  const frame = useCurrentFrame();
  const {
    fps,
    width: compositionWidth,
    height: compositionHeight,
  } = useVideoConfig();
  const effectiveCurrentTimeInSeconds =
    currentTimeInSeconds ?? frame / fps;

  const activeHighlights = useMemo(() => {
    return highlights.filter(
      (highlight) =>
        highlight.type !== 'focus-area' &&
        highlight.type !== 'freeze' &&
        highlight.position != null &&
        isHighlightActiveAtTime(highlight, effectiveCurrentTimeInSeconds),
    );
  }, [effectiveCurrentTimeInSeconds, highlights]);

  return (
    <AbsoluteFill style={{pointerEvents: 'none'}}>
      {activeHighlights.map((highlight, index) => {
        const opacity = getMarkerOpacityAtTime({
          highlight,
          currentTimeInSeconds: effectiveCurrentTimeInSeconds,
          defaultFadeIn,
          defaultFadeOut,
        });
        if (highlight.position == null) {
          return null;
        }

        const rect = resolveMarkerRect({
          position: highlight.position,
          compositionWidth,
          compositionHeight,
          coordinateWidth,
          coordinateHeight,
        });

        if (opacity <= 0) {
          return null;
        }

        if (highlight.type === 'arrow') {
          return (
            <ArrowMarker
              key={`${highlight.type}-${highlight.startTime}-${index}`}
              x={rect.x}
              y={rect.y}
              width={rect.width}
              height={rect.height}
              color={highlight.borderColor ?? DEFAULT_ARROW_COLOR}
              rotation={highlight.rotation ?? 0}
              opacity={opacity}
            />
          );
        }

        const width = rect.width;
        const height = rect.height;
        const borderRadius =
          highlight.type === 'circle'
            ? Math.min(width, height) / 2
            : highlight.borderRadius ?? 0;

        return (
          <ShapeMarker
            key={`${highlight.type}-${highlight.startTime}-${index}`}
            x={rect.x}
            y={rect.y}
            width={width}
            height={height}
            borderRadius={borderRadius}
            borderWidth={highlight.borderWidth ?? 10}
            borderColor={highlight.borderColor ?? DEFAULT_MARKER_COLOR}
            rotation={highlight.rotation ?? 0}
            opacity={opacity}
          />
        );
      })}
    </AbsoluteFill>
  );
};
